import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Field } from "@/components/admin/fields";
import { useAuth } from "@/context/AuthContext";

const ROLE_STYLES = {
  admin: "bg-gold text-white",
  staff: "bg-zinc-900 text-white",
  customer: "bg-zinc-100 text-zinc-500",
};

export const UsersTab = ({ FormModal }) => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(null);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", form);
      toast.success(`${form.role} account created for ${form.email}`);
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.put(`/admin/users/${u.id}`, { role });
      toast.success(`${u.email} is now ${role}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="users-tab">
      <div className="flex items-center justify-between mb-8">
        <p className="text-sm text-zinc-500 max-w-xl">
          Add your team. <span className="text-gold">Admin</span> controls everything · <span className="text-zinc-800 font-medium">Staff</span> manages orders &amp; shipping only · Customers shop.
        </p>
        <button onClick={() => setForm({ name: "", email: "", password: "", role: "staff" })} data-testid="user-add-btn" className="flex items-center gap-2 bg-gold text-white text-[10px] uppercase tracking-[0.3em] px-6 py-3 hover:bg-champagne transition-colors duration-300">
          <Plus size={14} /> Add User
        </button>
      </div>
      <div className="border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[680px]">
          <thead>
            <tr className="border-b border-zinc-200 text-[10px] uppercase tracking-[0.25em] text-zinc-400">
              <th className="text-left px-5 py-4 font-medium">Name</th>
              <th className="text-left px-5 py-4 font-medium">Email</th>
              <th className="text-left px-5 py-4 font-medium">Role</th>
              <th className="text-left px-5 py-4 font-medium">Joined</th>
              <th className="text-right px-5 py-4 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-200" data-testid={`user-row-${u.email}`}>
                <td className="px-5 py-4 text-zinc-800">{u.name || "—"}</td>
                <td className="px-5 py-4 text-zinc-500">{u.email}</td>
                <td className="px-5 py-4">
                  <select
                    value={u.role}
                    onChange={(e) => changeRole(u, e.target.value)}
                    disabled={u.id === me?.id}
                    data-testid={`user-role-${u.email}`}
                    className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border-0 cursor-pointer ${ROLE_STYLES[u.role] || ROLE_STYLES.customer}`}
                  >
                    <option value="admin">Admin</option>
                    <option value="staff">Staff</option>
                    <option value="customer">Customer</option>
                  </select>
                </td>
                <td className="px-5 py-4 text-zinc-400 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN") : "—"}</td>
                <td className="px-5 py-4 text-right">
                  {u.id !== me?.id && (
                    <button onClick={() => remove(u)} data-testid={`user-delete-${u.email}`} className="p-2 text-zinc-400 hover:text-red-500 transition-colors duration-200" aria-label="Remove">
                      <Trash2 size={15} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <FormModal title="Add Team Member" onClose={() => setForm(null)} testid="user-form-modal">
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Full Name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="user-form-name" />
            <Field label="Email" required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="user-form-email" />
            <Field label="Password" required type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="user-form-password" />
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Role</label>
              <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} data-testid="user-form-role" className="w-full bg-zinc-100 border border-zinc-200 text-zinc-900 text-sm px-4 py-3 focus:outline-none focus:border-gold/60">
                <option value="staff">Staff — orders &amp; shipping</option>
                <option value="admin">Admin — full control</option>
                <option value="customer">Customer</option>
              </select>
            </div>
            <button type="submit" data-testid="user-form-submit" className="sm:col-span-2 bg-gold text-white text-xs uppercase tracking-[0.3em] py-4 hover:bg-champagne transition-colors duration-300">
              Create Account
            </button>
          </form>
        </FormModal>
      )}
    </div>
  );
};
